import React, { useRef, useState } from "react";
import { Box, Typography, SxProps } from "@mui/material";
import { createStyles, palette, spacing } from "../../../../styles/theme";
import DesignService from "../../../../service/DesignService";
import HoverableIcon from "../../../../common/components/Icon/HoverableIcon";
import LockDesignModal from "./LockDesignModal";
import { useAuth } from "../../../../common/hooks/useAuth";

interface LockedDesignProps {
  designId: string;
  locked: boolean;
  lockedVersion?: string;
  onUpdate: () => Promise<void>;
  customStyles?: SxProps;
  backgroundPersist?: boolean;
}

const useStyles = (isAdmin: boolean) =>
  createStyles({
    locked: {
      pl: "4px",
      pr: "8px",
      py: "2px",
      display: "flex",
      alignItems: "center",
      gap: spacing.xxsDesktop,
      borderRadius: "100px",
      cursor: isAdmin ? "pointer" : "default",
      pointerEvents: isAdmin ? "auto" : "none",
      "&:hover": {
        backgroundColor: palette.grey.lightGrey,
      },
    },
  });

const LockedDesign = ({
  designId,
  locked,
  lockedVersion = "",
  onUpdate,
  customStyles = {},
  backgroundPersist = false,
}: LockedDesignProps) => {
  const { isAdmin } = useAuth();
  const [lockDesignModal, setLockDesignModal] = useState(false);
  const lockIconRef = useRef<HTMLDivElement | null>(null);
  const styles = useStyles(isAdmin);

  // Only show if its locked
  if (!locked) {
    return null;
  }

  const handleLockClick = async () => {
    if (!isAdmin) {
      return;
    }

    if (locked) {
      // Unlock the design
      await DesignService.lockDesign(designId, false, lockedVersion);
      await onUpdate();
    } else {
      // Show the lock modal
      setLockDesignModal(true);
    }
  };

  return (
    <>
      <Box
        ref={lockIconRef}
        sx={{
          ...styles.locked,
          ...customStyles,
          backgroundColor: backgroundPersist
            ? palette.grey.lightGrey
            : "transparent",
        }}
        data-test="lockedDesign.container"
        onClick={handleLockClick}
      >
        <HoverableIcon
          defaultIcon={locked ? "lock" : "lock_open"}
          hoverIcon={locked ? "lock_open" : "lock"}
          targetRef={lockIconRef}
          color={palette.grey.primary}
          size={12}
          data-test="lockedDesign.icon"
        />
        <Typography variant="textXs" color={palette.grey.primary}>
          {lockedVersion}
        </Typography>
      </Box>
      <LockDesignModal
        open={lockDesignModal}
        onClose={() => setLockDesignModal(false)}
        designId={designId}
        oldVersion={lockedVersion}
        onUpdate={onUpdate}
      />
    </>
  );
};

export default LockedDesign;
